import axios from "axios";
import { useState } from "react";
import Button from "../common/Button";
import ListInput from "../Settings/ListInput";

interface EmailInviteInputProps {
  maxSeats?: number;
  onInvite?: (emails: string[]) => void;
  className?: string;
  placeholder?: string;
  onSuccess?: (results: any[]) => void;
  onError?: (error: string) => void;
  endpoint?: string;
}

const EmailInviteInput: React.FC<EmailInviteInputProps> = ({
  maxSeats = Infinity,
  onInvite,
  onSuccess,
  onError,
  className = "",
  placeholder = "Enter email addresses...",
  endpoint = "/api/agency/invite",
}) => {
  const [emails, setEmails] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleEmailsChange = (newEmails: string[]) => {
    // Validate new emails
    const invalidEmails = newEmails.filter((email) => !validateEmail(email));
    if (invalidEmails.length > 0) {
      setError(`"${invalidEmails[0]}" is not a valid email address`);
      return;
    }

    setEmails(newEmails);
    setError(null);
  };

  const handleInvite = async () => {
    if (emails.length === 0) {
      setError("Please add at least one email address");
      return;
    }

    setIsSubmitting(true);
    try {
      const { data } = await axios.post(
        endpoint,
        { emails },
        {
          headers: {
            "Content-Type": "application/json",
          },
          withCredentials: true,
        }
      );

      // Check for per-email errors in results
      const results = data.results || [];
      const failed = results.filter((r: any) => r.status === "error");
      const succeeded = results.filter((r: any) => r.status !== "error");

      if (failed.length > 0) {
        // Keep failed emails in the input
        const failedEmails = failed.map((r: any) => r.email);
        setEmails(failedEmails);

        // Build error message
        const errorMessages = failed.map(
          (r: any) => `${r.email}: ${r.error}`
        );
        setError(errorMessages.join(". "));
      } else {
        setEmails([]);
        setError(null);
      }

      if (succeeded.length > 0) {
        onSuccess?.(results);
        onInvite?.(succeeded.map((r: any) => r.email));
      }
    } catch (err) {
      const message = axios.isAxiosError(err)
        ? err.response?.data?.error || "Failed to send invitations"
        : "Failed to send invitations";
      setError(message);
      onError?.(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={`w-full ${className}`}>
      <div data-tour="agency-invite-emails">
      <ListInput
        label="Email Addresses"
        items={emails}
        onItemsChange={handleEmailsChange}
        placeholder={placeholder}
        maxItems={maxSeats}
        className="mb-4"
      />
      </div>

      {error && <p className="text-error text-xs">{error}</p>}

      <div className="flex justify-end mt-4" data-tour="agency-invite-send">
        <Button
          variant="primary"
          onClick={handleInvite}
          disabled={emails.length === 0 || isSubmitting}
        >
          {isSubmitting ? "Sending..." : "Invite"}
        </Button>
      </div>
    </div>
  );
};

export default EmailInviteInput;
